import React, { createContext, useContext, useEffect, useState } from "react";
import AuthApi, { AuthUser, LoginResult, isMfaChallenge } from "../api/AuthApi";

interface AuthCtx {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (mfaToken: string, code: string) => Promise<AuthUser>;
  refreshUser: () => Promise<void>;
  register: (b: { email: string; password: string; name?: string; role?: string }) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({
  user: null,
  loading: true,
  login: async () => ({} as LoginResult),
  verifyMfa: async () => ({} as AuthUser),
  refreshUser: async () => {},
  register: async () => ({} as AuthUser),
  logout: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always clear loading, even if /me rejects (network error / hang) — otherwise
    // the whole app is stuck behind the gate's loading state forever.
    AuthApi.me()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Returns the raw result: an AuthUser (logged in) or an MfaChallenge (needs 2nd factor).
  // After any successful auth, hydrate the authoritative user from /me — the
  // /login and /register responses don't carry mfaEnabled/mfaRequired, so the
  // enrolment gate would otherwise never fire until a page refresh.
  const login = async (email: string, password: string) => {
    const res = await AuthApi.login({ email, password });
    if (isMfaChallenge(res)) return res; // do NOT set user yet
    setUser(await AuthApi.me());
    return res;
  };
  const verifyMfa = async (mfaToken: string, code: string) => {
    const u = await AuthApi.mfaVerify(mfaToken, code);
    setUser(await AuthApi.me());
    return u;
  };
  const refreshUser = async () => { setUser(await AuthApi.me()); };
  const register = async (b: { email: string; password: string; name?: string; role?: string }) => {
    const u = await AuthApi.register(b);
    setUser(await AuthApi.me());
    return u;
  };
  const logout = async () => {
    await AuthApi.logout();
    setUser(null);
  };

  return <Ctx.Provider value={{ user, loading, login, verifyMfa, refreshUser, register, logout }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
